import { PayloadSizeBucket, StompClock } from './types';

export const STOMP_CONFIG = {
  /** Incoming heartbeat timeout (ms). Matches the previous middleware default. */
  heartbeatIncomingMs: 12000,
  /** Outgoing heartbeat interval (ms). Matches the previous middleware default. */
  heartbeatOutgoingMs: 10000,
  /**
   * Abort a socket that opens but never delivers CONNECTED. Without this a
   * half-open connection leaves the client activating forever and no reconnect
   * is ever scheduled.
   */
  connectionTimeoutMs: 10000,
  /** Base delay for the first reconnect attempt. First connect is not delayed. */
  reconnectInitialDelayMs: 500,
  /** Cap for exponential reconnect backoff. */
  reconnectMaxDelayMs: 30000,
  /** ± fraction applied to each reconnect delay. */
  reconnectJitterRatio: 0.2,
  /** Max disconnected ENQUEUE_* commands retained. */
  outboundMaxQueuedCount: 50,
  /** Max disconnected ENQUEUE_* payload bytes retained. */
  outboundMaxQueuedBytes: 256 * 1024,
  /** Max age of a queued ENQUEUE_* command. */
  outboundTtlMs: 30 * 1000,
  /** Commands published per reconnect flush turn before yielding. */
  outboundFlushBatchSize: 10,
  /** Subscriptions restored per turn after reconnect before yielding. */
  restoreBatchSize: 25,
  /** Max STOMP frame body we will parse or publish. */
  maxPayloadBytes: 256 * 1024,
  /** Inbound process+dispatch duration that records a slow-handler metric. */
  inboundSlowHandlerMs: 50,
  /**
   * Upper bound on inbound dispatch latency. Animation frames do not run in a
   * hidden tab, so a timer races the frame to keep the pending buffer draining.
   */
  inboundMaxLatencyMs: 250,
  /** Pending inbound messages that force a flush instead of waiting for a frame. */
  inboundMaxPendingItems: 500,
} as const;

/**
 * UTF-8 byte length without allocating. `TextEncoder#encode` would copy the
 * whole body into a byte array only to read `.length`, which is the last thing
 * a per-message hot path should do.
 */
export function utf8ByteLength(value: string): number {
  let bytes = 0;
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (code < 0x80) {
      bytes += 1;
    } else if (code < 0x800) {
      bytes += 2;
    } else if (code >= 0xd800 && code <= 0xdbff) {
      const next = value.charCodeAt(i + 1);
      if (next >= 0xdc00 && next <= 0xdfff) {
        // Surrogate pair: two UTF-16 units encode to four UTF-8 bytes.
        bytes += 4;
        i++;
      } else {
        bytes += 3;
      }
    } else {
      bytes += 3;
    }
  }
  return bytes;
}

/**
 * Payload cap check that only walks the string when the UTF-16 length bounds
 * (1..3 bytes per code unit) leave the answer undecided.
 */
export function exceedsUtf8Limit(value: string, maxBytes: number): boolean {
  if (value.length * 3 <= maxBytes) return false;
  if (value.length > maxBytes) return true;
  return utf8ByteLength(value) > maxBytes;
}

export function payloadSizeBucket(bytes: number): PayloadSizeBucket {
  if (bytes < 1024) return 'lt1kb';
  if (bytes < 8 * 1024) return 'lt8kb';
  if (bytes < 64 * 1024) return 'lt64kb';
  if (bytes < 256 * 1024) return 'lt256kb';
  return 'gte256kb';
}

export function nextReconnectDelay(
  attempt: number,
  random: () => number = Math.random
): number {
  const exponential = Math.min(
    STOMP_CONFIG.reconnectInitialDelayMs * Math.pow(2, Math.max(0, attempt)),
    STOMP_CONFIG.reconnectMaxDelayMs
  );
  const jitterRange = exponential * STOMP_CONFIG.reconnectJitterRatio;
  const jitter = (random() * 2 - 1) * jitterRange;
  return Math.min(
    STOMP_CONFIG.reconnectMaxDelayMs,
    Math.max(0, Math.round(exponential + jitter))
  );
}

export function createDefaultStompClock(): StompClock {
  // Frame ids and timeout ids share a numeric space, so `cancelFrame` must undo
  // whatever `scheduleFrame` used. Deciding once, rather than re-testing the
  // global at cancel time, is what makes that guarantee hold.
  const hasFrames =
    typeof requestAnimationFrame === 'function' &&
    typeof cancelAnimationFrame === 'function';

  return {
    now: () => Date.now(),
    setTimeout: (fn: () => void, ms: number) => setTimeout(fn, ms),
    clearTimeout: (id: any) => clearTimeout(id),
    scheduleFrame: (fn: () => void) =>
      hasFrames ? requestAnimationFrame(fn) : setTimeout(fn, 0),
    cancelFrame: (id: any) => {
      if (hasFrames) {
        cancelAnimationFrame(id);
        return;
      }
      clearTimeout(id);
    },
  };
}
